import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Clock, Banknote, Tag, Cake, Save, X, Search, Info } from 'lucide-react';
import { getSetting, putSetting } from '../../../services/settings';
import { getCashboxConfig, putCashboxConfig, CashboxConfig, money } from '../../finances/api';
import { listMembers, getMember, MemberRow } from '../../members/api';
import { useToast } from '../../../context/ToastContext';
import VitrinaInventarioCard from '../components/VitrinaInventarioCard';
import VitrinaEquipoCard from '../components/VitrinaEquipoCard';
import { useAuth } from '../../../context/AuthContext';
import { Skeleton } from '../../../components/Skeleton';

// MI ESTUDIO — las reglas con las que trabaja el estudio, en un solo sitio.
//
// Regla de esta pantalla (17-sep-2026): aquí SOLO entra lo que de verdad se guarda y el sistema
// obedece. Una pantalla de ajustes con la mitad de los botones muertos enseña a desconfiar de
// ella. Lo que aún vive en el código se enumera al final, por escrito y sin botón.
//
// Cada tarjeta guarda por su cuenta: son ajustes independientes y un «Guardar» global obligaría
// a releer todo para saber qué se tocó.

const DIAS = [{ d: 1, l: 'L' }, { d: 2, l: 'M' }, { d: 3, l: 'X' }, { d: 4, l: 'J' }, { d: 5, l: 'V' }, { d: 6, l: 'S' }, { d: 0, l: 'D' }];

const inputCls = 'rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';
const cardCls = 'rounded-2xl border border-line bg-surface p-5 shadow-sm';

function Card({ icon: Icon, title, hint, children }: { icon: typeof Clock; title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className={cardCls}>
      <h2 className="flex items-center gap-2 font-display text-base font-bold text-content"><Icon size={17} className="text-brand-blue" /> {title}</h2>
      <p className="mb-4 mt-0.5 text-xs text-content-3">{hint}</p>
      {children}
    </section>
  );
}

function BotonGuardar({ onClick, saving, dirty }: { onClick: () => void; saving: boolean; dirty: boolean }) {
  return (
    <button onClick={onClick} disabled={saving || !dirty}
      className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent-solid px-3.5 py-2 text-sm font-semibold text-on-accent transition disabled:bg-line disabled:text-content-3">
      <Save size={15} /> {saving ? 'Guardando…' : dirty ? 'Guardar cambios' : 'Sin cambios'}
    </button>
  );
}

export default function MiEstudioPage() {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // ── Horario y turnos (letsmove_settings.cashbox_config) ──
  const [cfg, setCfg] = useState<CashboxConfig | null>(null);
  const [cfgOrig, setCfgOrig] = useState<string>('');
  const [savingCfg, setSavingCfg] = useState(false);

  // ── Cobranza ──
  const [dueDay, setDueDay] = useState('3');
  const [graceMode, setGraceMode] = useState<'fixed' | 'first_session'>('fixed');
  const [graceDays, setGraceDays] = useState('0');
  const [reglaOrig, setReglaOrig] = useState('');
  const [savingRegla, setSavingRegla] = useState(false);
  const [multa, setMulta] = useState(''); const [multaOrig, setMultaOrig] = useState('');
  const [savingMulta, setSavingMulta] = useState(false);
  // Los 3 interruptores de la multa (letsmove_settings.late_fee_rules, 23-sep-2026)
  const [tolDias, setTolDias] = useState('0');
  const [sinTransf, setSinTransf] = useState(false);
  const [sinCiclo, setSinCiclo] = useState(false);
  const [ventanaTransf, setVentanaTransf] = useState('3');   // 25-sep-2026: días tras vencer en que la transferencia se perdona sola
  const [reglasOrig, setReglasOrig] = useState('');
  const [savingReglas, setSavingReglas] = useState(false);
  const [excl, setExcl] = useState<number[]>([]); const [exclOrig, setExclOrig] = useState('');
  const [exclNombres, setExclNombres] = useState<Record<number, string>>({});
  const pedidos = useRef<Set<number>>(new Set());
  const [buscando, setBuscando] = useState(''); const [cands, setCands] = useState<MemberRow[]>([]);
  const [savingExcl, setSavingExcl] = useState(false);

  // ── Precios de clase suelta ──
  const [precioSem, setPrecioSem] = useState(''); const [precioSab, setPrecioSab] = useState('');
  const [preciosOrig, setPreciosOrig] = useState(''); const [savingPrecios, setSavingPrecios] = useState(false);

  // ── Felicitación de cumpleaños ──
  const [cumple, setCumple] = useState(''); const [cumpleOrig, setCumpleOrig] = useState('');
  const [savingCumple, setSavingCumple] = useState(false);

  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const [c, tp, lf, bx, bm, dr, lr] = await Promise.all([
        getCashboxConfig(),
        getSetting('trial_prices').catch(() => null),
        getSetting('late_fee_amount').catch(() => null),
        getSetting('billing_excluded_contacts').catch(() => null),
        getSetting('birthday_message').catch(() => null),
        getSetting('billing_due_rule').catch(() => null),
        getSetting('late_fee_rules').catch(() => null),
      ]);
      if (lr) {
        const r = JSON.parse(lr.value || '{}');
        setTolDias(String(r.tolerance_days ?? 0)); setSinTransf(!!r.skip_wire); setSinCiclo(!!r.skip_cycle); setVentanaTransf(String(r.wire_window_days ?? 3));
        setReglasOrig(JSON.stringify({ t: Number(r.tolerance_days ?? 0), w: !!r.skip_wire, c: !!r.skip_cycle, v: Number(r.wire_window_days ?? 3) }));
      }
      if (dr) {
        const r = JSON.parse(dr.value || '{}');
        setDueDay(String(r.due_day ?? 3)); setGraceMode(r.grace_mode === 'first_session' ? 'first_session' : 'fixed');
        setGraceDays(String(r.grace_days ?? 0));
        setReglaOrig(JSON.stringify({ d: Number(r.due_day ?? 3), m: r.grace_mode || 'fixed', g: Number(r.grace_days ?? 0) }));
      }
      setCfg(c); setCfgOrig(JSON.stringify(c));
      if (tp) { const p = JSON.parse(tp.value || '{}'); setPrecioSem(String(p.weekday ?? '')); setPrecioSab(String(p.saturday ?? '')); setPreciosOrig(JSON.stringify({ w: p.weekday, s: p.saturday })); }
      if (lf) { setMulta(String(Number(lf.value) || 0)); setMultaOrig(String(Number(lf.value) || 0)); }
      if (bx) { const ids: number[] = JSON.parse(bx.value || '[]'); setExcl(ids); setExclOrig(JSON.stringify(ids)); }
      if (bm) { setCumple(bm.value || ''); setCumpleOrig(bm.value || ''); }
    } catch (e: any) { toast.error(e?.message || 'No se pudo cargar la configuración'); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { cargar(); }, [cargar]);

  // Nombres de los contactos excluidos, para no enseñar ids pelones.
  // 🚨 Cada id se pide UNA sola vez (ref `pedidos`): la primera versión buscaba en una lista de
  // 500 alumnas y, si un id no aparecía ahí —una cuenta de prueba desactivada, por ejemplo—,
  // `faltan` nunca se vaciaba y la pantalla se quedaba pidiendo la lista en bucle.
  useEffect(() => {
    const faltan = excl.filter((id) => !exclNombres[id] && !pedidos.current.has(id));
    if (!faltan.length) return;
    faltan.forEach((id) => pedidos.current.add(id));
    Promise.all(faltan.map((id) => getMember(id).then((r) => [id, `${r.data.firstname} ${r.data.lastname || ''}`.trim()] as const).catch(() => [id, `#${id}`] as const)))
      .then((pares) => setExclNombres((prev) => ({ ...prev, ...Object.fromEntries(pares) })));
  }, [excl, exclNombres]);

  // Buscador para añadir a la lista de excluidos
  useEffect(() => {
    if (buscando.trim().length < 2) { setCands([]); return; }
    const t = setTimeout(() => {
      listMembers({ search: buscando.trim(), status: 'all', page_size: 6 }).then((r) => setCands(r.data)).catch(() => setCands([]));
    }, 250);
    return () => clearTimeout(t);
  }, [buscando]);

  async function guardarCfg() {
    if (!cfg) return;
    setSavingCfg(true);
    try {
      const { isDefault, ...limpio } = cfg as CashboxConfig & { isDefault?: boolean };
      const r = await putCashboxConfig(limpio);
      setCfg(r); setCfgOrig(JSON.stringify(r));
      toast.success('Horario y turnos guardados');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingCfg(false); }
  }
  async function guardarRegla() {
    setSavingRegla(true);
    try {
      await putSetting('billing_due_rule', JSON.stringify({ due_day: Number(dueDay), grace_mode: graceMode, grace_days: Number(graceDays) }));
      setReglaOrig(JSON.stringify({ d: Number(dueDay), m: graceMode, g: Number(graceDays) }));
      toast.success('Regla de vencimiento guardada · se aplica a los cargos que se generen desde ahora');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingRegla(false); }
  }
  async function guardarMulta() {
    setSavingMulta(true);
    try { await putSetting('late_fee_amount', String(Number(multa) || 0)); setMultaOrig(String(Number(multa) || 0)); toast.success('Importe del pago tardío guardado'); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingMulta(false); }
  }
  async function guardarReglas() {
    setSavingReglas(true);
    try {
      await putSetting('late_fee_rules', JSON.stringify({ tolerance_days: Number(tolDias) || 0, skip_wire: sinTransf, skip_cycle: sinCiclo, wire_window_days: Number(ventanaTransf) || 0 }));
      setReglasOrig(JSON.stringify({ t: Number(tolDias) || 0, w: sinTransf, c: sinCiclo, v: Number(ventanaTransf) || 0 }));
      toast.success('Reglas del pago tardío guardadas · el proceso de mañana ya las usa');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingReglas(false); }
  }
  async function guardarExcl(lista = excl) {
    setSavingExcl(true);
    try { await putSetting('billing_excluded_contacts', JSON.stringify(lista)); setExclOrig(JSON.stringify(lista)); toast.success('Lista actualizada'); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingExcl(false); }
  }
  async function guardarPrecios() {
    setSavingPrecios(true);
    try {
      await putSetting('trial_prices', JSON.stringify({ weekday: Number(precioSem) || 0, saturday: Number(precioSab) || 0 }));
      setPreciosOrig(JSON.stringify({ w: Number(precioSem), s: Number(precioSab) }));
      toast.success('Precios de clase suelta guardados');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingPrecios(false); }
  }
  async function guardarCumple() {
    setSavingCumple(true);
    try { await putSetting('birthday_message', cumple); setCumpleOrig(cumple); toast.success('Mensaje guardado'); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSavingCumple(false); }
  }

  if (!isAdmin) return <p className="text-sm text-content-3">Esta sección es solo para administración.</p>;
  if (loading || !cfg) return <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-2xl" />)}</div>;

  const dirtyCfg = JSON.stringify(cfg) !== cfgOrig;
  const dirtyPrecios = JSON.stringify({ w: Number(precioSem), s: Number(precioSab) }) !== preciosOrig;

  return (
    <div>
      <h2 className="mb-1 flex items-center gap-2 font-display text-2xl font-bold text-content">
        <Building2 className="text-brand-blue" size={24} /> Mi Estudio
      </h2>
      <p className="mb-5 text-sm text-content-3">Las reglas con las que trabaja el sistema. Lo que cambies aquí se aplica solo, sin tocar nada más.</p>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* ── HORARIO Y TURNOS ───────────────────────────────────────────── */}
        <Card icon={Clock} title="Horario y turnos"
          hint="Marca el turno de cada caja y el rango que se ve en el calendario.">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Abre</label>
              <input type="time" value={cfg.openingTime} onChange={(e) => setCfg({ ...cfg, openingTime: e.target.value })} className={`${inputCls} w-full`} />
            </div>
            <div>
              <label className={labelCls}>Cierra</label>
              <input type="time" value={cfg.closingTime} onChange={(e) => setCfg({ ...cfg, closingTime: e.target.value })} className={`${inputCls} w-full`} />
            </div>
          </div>
          <div className="mt-3">
            <label className={labelCls}>Días de operación</label>
            <div className="flex flex-wrap gap-1.5">
              {DIAS.map(({ d, l }) => {
                const on = cfg.workingDays.includes(d);
                return (
                  <button key={d} type="button"
                    onClick={() => setCfg({ ...cfg, workingDays: on ? cfg.workingDays.filter((x) => x !== d) : [...cfg.workingDays, d].sort() })}
                    className={`h-9 w-9 rounded-lg border text-sm font-semibold transition ${on ? 'border-accent-solid bg-accent-solid/15 text-accent-ink' : 'border-line bg-surface text-content-3'}`}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-3">
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
              <span className="text-sm text-content-2">Dos turnos al día</span>
              <input type="checkbox" checked={!!cfg.cashShiftSplit}
                onChange={(e) => setCfg({ ...cfg, cashShiftSplit: e.target.checked ? '15:00' : null })}
                className="h-4 w-4 rounded border-line" />
            </label>
            {cfg.cashShiftSplit && (
              <div className="mt-2">
                <label className={labelCls}>El turno de la tarde empieza a las</label>
                <input type="time" value={cfg.cashShiftSplit} onChange={(e) => setCfg({ ...cfg, cashShiftSplit: e.target.value })} className={inputCls} />
              </div>
            )}
          </div>
          <div className="mt-3 space-y-2">
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
              <span className="text-sm text-content-2">Mandar el corte por WhatsApp al cerrar</span>
              <input type="checkbox" checked={cfg.corte.enabled}
                onChange={(e) => setCfg({ ...cfg, corte: { ...cfg.corte, enabled: e.target.checked } })}
                className="h-4 w-4 rounded border-line" />
            </label>
            {cfg.corte.enabled && (
              <>
                <div>
                  <label className={labelCls}>WhatsApp que recibe el corte</label>
                  {/* 🚨 Hasta 15 dígitos, como el backend: el número guardado hoy es 526861627189
                      (con lada de país) y un tope de 10 lo habría dejado en «5268616271». */}
                  <input value={cfg.corte.phone} onChange={(e) => setCfg({ ...cfg, corte: { ...cfg.corte, phone: e.target.value.replace(/\D/g, '').slice(0, 15) } })}
                    placeholder="6861234567" className={`${inputCls} w-full`} />
                  <p className="mt-1 text-xs text-content-3">10 dígitos, o con lada de país si es un número de fuera.</p>
                </div>
                <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
                  <span className="text-sm text-content-2">Avisar también al ABRIR la caja</span>
                  <input type="checkbox" checked={cfg.corte.apertura}
                    onChange={(e) => setCfg({ ...cfg, corte: { ...cfg.corte, apertura: e.target.checked } })}
                    className="h-4 w-4 rounded border-line" />
                </label>
              </>
            )}
          </div>
          <BotonGuardar onClick={guardarCfg} saving={savingCfg} dirty={dirtyCfg} />
        </Card>

        {/* ── COBRANZA ───────────────────────────────────────────────────── */}
        <Card icon={Banknote} title="Cobranza"
          hint="Lo que el sistema cobra solo. El pago tardío lo aplica el proceso diario a quien va vencido.">
          <div className="mb-5 border-b border-line pb-4">
            <label className={labelCls}>La mensualidad vence</label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-content-2">el día</span>
              <input type="number" min="1" max="28" value={dueDay} onChange={(e) => setDueDay(e.target.value)} className={`${inputCls} w-16 text-center`} />
              <span className="text-sm text-content-2">de cada mes</span>
            </div>
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
              <input type="checkbox" checked={graceMode === 'first_session'}
                onChange={(e) => setGraceMode(e.target.checked ? 'first_session' : 'fixed')}
                className="mt-0.5 h-4 w-4 rounded border-line" />
              <span className="text-sm text-content-2">
                <b className="text-content">…o en su primera clase del mes, si es más tarde</b>
                <span className="mt-0.5 block text-xs text-content-3">
                  Quien solo viene en sábado no puede pagar en ventanilla antes de su primera clase.
                  Con esto, a esa alumna se le cobra desde ese día; a quien tiene clase el día 1 o 2 no le cambia nada.
                </span>
              </span>
            </label>
            {graceMode === 'first_session' && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-sm text-content-2">y además</span>
                <input type="number" min="0" max="15" value={graceDays} onChange={(e) => setGraceDays(e.target.value)} className={`${inputCls} w-16 text-center`} />
                <span className="text-sm text-content-2">día(s) de gracia</span>
              </div>
            )}
            <p className="mt-2 text-xs text-content-3">
              Solo afecta a los cargos que se generen <b>a partir de ahora</b>: los meses ya generados conservan su fecha.
            </p>
            <BotonGuardar onClick={guardarRegla} saving={savingRegla}
              dirty={JSON.stringify({ d: Number(dueDay), m: graceMode, g: Number(graceDays) }) !== reglaOrig} />
          </div>

          <div>
            <label className={labelCls}>Pago tardío</label>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-content-3">$</span>
              <input type="number" min="0" step="10" value={multa} onChange={(e) => setMulta(e.target.value)} className={`${inputCls} w-32`} />
            </div>
            <p className="mt-1 text-xs text-content-3">Se cobra un solo pago tardío por alumna y mes, nunca uno por día de atraso.</p>
            <BotonGuardar onClick={guardarMulta} saving={savingMulta} dirty={String(Number(multa) || 0) !== multaOrig} />
          </div>

          <div className="mt-5 border-t border-line pt-4">
            <label className={labelCls}>Cuándo NO se pone el pago tardío</label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-content-2">Esperar</span>
              <input type="number" min="0" max="15" value={tolDias} onChange={(e) => setTolDias(e.target.value)} className={`${inputCls} w-16 text-center`} />
              <span className="text-sm text-content-2">día(s) después del vencimiento antes de ponerlo</span>
            </div>
            <p className="mt-1 text-xs text-content-3">Con 0, el pago tardío entra al día siguiente de vencer. La fecha de vencimiento no cambia; solo se espera más para ponerlo.</p>
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
              <input type="checkbox" checked={sinTransf} onChange={(e) => setSinTransf(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line" />
              <span className="text-sm text-content-2">
                <b className="text-content">Quien paga por transferencia no lleva pago tardío</b>
                <span className="mt-0.5 block text-xs text-content-3">
                  La transferencia se registra días después de que la alumna la hace. Si el pago tardío ya se había puesto,
                  se quita sola cuando entra el pago de la mensualidad por transferencia, con el motivo a la vista.
                </span>
              </span>
            </label>
            {sinTransf && (
              <div className="mt-2 ml-7 flex flex-wrap items-center gap-2">
                <span className="text-sm text-content-2">Solo si se registra hasta</span>
                <input type="number" min="0" max="31" value={ventanaTransf} onChange={(e) => setVentanaTransf(e.target.value)} className={`${inputCls} w-16 text-center`} />
                <span className="text-sm text-content-2">día(s) después del vencimiento</span>
                <p className="w-full text-xs text-content-3">Si llega más tarde, el pago tardío se queda y aparece en Finanzas → Pagos tardíos como «Transferencia tardía» para que decidas con el comprobante: Perdonar o Mantener.</p>
              </div>
            )}
            <label className="mt-2 flex cursor-pointer items-start gap-2.5 rounded-xl border border-line bg-surface-2/60 px-3 py-2.5">
              <input type="checkbox" checked={sinCiclo} onChange={(e) => setSinCiclo(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line" />
              <span className="text-sm text-content-2">
                <b className="text-content">Quien tiene cobro desfasado no lleva pago tardío</b>
                <span className="mt-0.5 block text-xs text-content-3">
                  Alumnas que entraron a medio mes y pagan en su propio día, no el {dueDay || 3}. Aplica a los cargos
                  generados desde octubre.
                </span>
              </span>
            </label>
            <BotonGuardar onClick={guardarReglas} saving={savingReglas}
              dirty={JSON.stringify({ t: Number(tolDias) || 0, w: sinTransf, c: sinCiclo, v: Number(ventanaTransf) || 0 }) !== reglasOrig} />
          </div>


          <div className="mt-5 border-t border-line pt-4">
            <label className={labelCls}>Prórrogas y excepciones de un mes</label>
            <p className="text-xs text-content-3">
              Dar prórroga a un mes, exentar a una alumna o perdonar un pago tardío se hace mes a mes, viendo quién
              lleva pago tardío y por qué: <Link to="/finanzas?tab=multas" className="font-medium text-brand-blue hover:underline">Finanzas → Pagos tardíos</Link>.
            </p>
          </div>

          <div className="mt-5 border-t border-line pt-4">
            <label className={labelCls}>Cuentas que no entran a cobranza</label>
            <p className="mb-2 text-xs text-content-3">El salón en renta y las cuentas de prueba: no cuentan como alumnas ni en el pronóstico ni en «Por cobrar».</p>
            <div className="flex flex-wrap gap-1.5">
              {excl.length === 0 && <span className="text-xs text-content-3">Ninguna.</span>}
              {excl.map((id) => (
                <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-1 text-xs text-content-2">
                  {exclNombres[id] || `#${id}`}
                  <button type="button" onClick={() => { const l = excl.filter((x) => x !== id); setExcl(l); guardarExcl(l); }}
                    disabled={savingExcl} className="text-content-3 hover:text-content" aria-label="Quitar"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="relative mt-2">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
              <input value={buscando} onChange={(e) => setBuscando(e.target.value)} placeholder="Añadir una cuenta…" className={`${inputCls} w-full pl-8`} />
              {cands.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                  {cands.filter((m) => !excl.includes(m.account_id)).map((m) => (
                    <button key={m.account_id} type="button"
                      onClick={() => { const l = [...excl, m.account_id]; setExcl(l); setExclNombres((p) => ({ ...p, [m.account_id]: `${m.firstname} ${m.lastname || ''}`.trim() })); setBuscando(''); setCands([]); guardarExcl(l); }}
                      className="block w-full px-3 py-2 text-left text-sm text-content transition hover:bg-surface-2">
                      {m.firstname} {m.lastname}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* ── INVENTARIO DE LA VITRINA (tarjeta autocontenida) ───────────── */}
        <VitrinaInventarioCard />

        {/* ── EQUIPO EN LA VITRINA: quién es staff y su descuento (Carlos 29-sep) ── */}
        <VitrinaEquipoCard />

        {/* ── PRECIOS DE CLASE SUELTA ────────────────────────────────────── */}
        <Card icon={Tag} title="Clase suelta"
          hint="Lo que recepción cobra a quien viene un día. La primera clase de un estilo sigue siendo gratis para las alumnas.">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Entre semana</label>
              <div className="flex items-center gap-1.5"><span className="text-sm text-content-3">$</span>
                <input type="number" min="0" step="10" value={precioSem} onChange={(e) => setPrecioSem(e.target.value)} className={`${inputCls} w-28`} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Sábado</label>
              <div className="flex items-center gap-1.5"><span className="text-sm text-content-3">$</span>
                <input type="number" min="0" step="10" value={precioSab} onChange={(e) => setPrecioSab(e.target.value)} className={`${inputCls} w-28`} />
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-content-3">
            Ahora mismo: {money(Number(precioSem) || 0)} entre semana · {money(Number(precioSab) || 0)} el sábado.
          </p>
          <BotonGuardar onClick={guardarPrecios} saving={savingPrecios} dirty={dirtyPrecios} />
        </Card>

        {/* ── CUMPLEAÑOS ─────────────────────────────────────────────────── */}
        <Card icon={Cake} title="Felicitación de cumpleaños"
          hint="El texto que se manda desde el botón «Felicitar». {nombre} se sustituye por el de la alumna.">
          <textarea value={cumple} onChange={(e) => setCumple(e.target.value)} rows={4}
            className={`${inputCls} w-full resize-y`} />
          <BotonGuardar onClick={guardarCumple} saving={savingCumple} dirty={cumple !== cumpleOrig} />
        </Card>
      </div>

      {/* Honestidad sobre lo que todavía NO se cambia desde aquí. */}
      <section className={`${cardCls} mt-4`}>
        <h2 className="flex items-center gap-2 font-display text-base font-bold text-content"><Info size={17} className="text-content-3" /> Todavía no se cambia desde aquí</h2>
        <p className="mb-3 mt-0.5 text-xs text-content-3">Estas reglas existen y funcionan, pero hoy solo se tocan en el código. Están en la lista para ir bajándolas aquí.</p>
        <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm text-content-2 sm:grid-cols-2">
          <li>• Cómo se paga la nómina (lo programado, redondeo a media hora)</li>
          <li>• Cuándo una clase se considera llena o floja</li>
          <li>• Marcar una clase como renta o ensayo (hoy se deduce del nombre)</li>
          <li>• Identidad del estudio: logo, colores y datos de los recibos</li>
        </ul>
      </section>
    </div>
  );
}
